import { z } from 'zod';
import { ERROR_CODES } from '../errors';

export const LOCALES = ['el', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const LocaleSchema = z.enum(LOCALES);

export const DEFAULT_TIMEZONE = 'Europe/Athens';
export const DEFAULT_LOCALE: Locale = 'el';
export const DEFAULT_COUNTRY = 'GR';

/** IANA timezone check (runtime Intl; both Node and browsers ship the full tz database). */
export function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const TimezoneSchema = z
  .string()
  .min(3)
  .max(64)
  .refine(isValidTimezone, { message: 'invalid_timezone' });

export const UuidSchema = z.uuid();
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
/** Calendar date without time, e.g. birthdays: YYYY-MM-DD. */
export const IsoDateSchema = z.iso.date();
export const CurrencySchema = z.string().length(3).toUpperCase();

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export const PaginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export function paginated<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

export const FILE_PURPOSES = [
  'import',
  'export',
  'dashboard_bundle',
  'logo',
  'attachment',
  'email_image',
  'other',
] as const;
export type FilePurpose = (typeof FILE_PURPOSES)[number];

export const ACTOR_TYPES = ['user', 'system', 'platform_admin', 'ai', 'public'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/** Header names used by the API. */
export const HEADERS = {
  tenant: 'X-Tenant-Id',
  idempotencyKey: 'Idempotency-Key',
  requestId: 'X-Request-Id',
} as const;
